import React, { useMemo, useState } from "react";

import { Input } from "../../../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../components/ui/select";
import { Textarea } from "../../../../components/ui/textarea";
import { ToggleGroup, ToggleGroupItem } from "../../../../components/ui/toggle-group";
import { WORKSPACE_TOOL_PROJECTION_STATUSES } from "../../projectionContract";
import ToolProjectionNotice from "./ToolProjectionNotice";
import { mayRender, resolveToolRenderStatus } from "./toolProjectionGuard";

const EMPTY_LIST = Object.freeze([]);


const HOUSEHOLD_SUBJECT_ID = "household";

// Same border/fill/focus treatment approved for Discovery's editable
// fields (frontend/src/features/agent-workspace/components/panels/
// DiscoveryPanel.jsx) — reused verbatim rather than inventing a second
// "Quote input" style. See that file's own comment for the full rationale
// (this app never activates the shadcn .dark class, so the unmodified
// defaults under-contrast against the dark Workspace glass surface).
const FIELD_CONTROL_CLASS =
  "h-8 rounded-[8px] border border-white/10 bg-white/5 text-xs text-slate-200 shadow-none " +
  "placeholder:text-slate-600 focus-visible:outline-none focus-visible:ring-1 " +
  "focus-visible:ring-indigo-400 focus-visible:border-indigo-400/40";
const FIELD_TEXTAREA_CLASS = FIELD_CONTROL_CLASS.replace("h-8", "min-h-[52px]");
// Household's derived fields use the same shape but a visibly dimmer fill
// and no focus ring, since they are not interactive — the agent should be
// able to tell at a glance that these are not editable.
const READONLY_CONTROL_CLASS =
  "h-8 cursor-default rounded-[8px] border border-white/10 bg-white/[0.03] text-xs text-slate-300 shadow-none";

function valuesToMap(values) {
  const map = new Map();
  values.forEach((entry) => {
    map.set(`${entry.field_id}::${entry.subject_id}::${entry.option_index}`, entry.value);
  });
  return map;
}

function controlId(field, subjectId, optionIndex) {
  return `quote-field-${field.id}--${subjectId}--${optionIndex}`;
}

function sortedProspectSubjects(subjects) {
  return subjects
    .filter((s) => s.kind === "prospect")
    .slice()
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
}

// Derives a Household field's value by summing every prospect subject's
// value for the SAME field at the SAME (Household's own selected) option —
// never each prospect's individually-selected UI option. Mirrors
// WORKSPACE-AUDIT-004 SS11/SS12's documented real-system rule for
// include_in_household input fields — the only aggregation path this first
// pass reproduces (no calculated-field aggregation yet, per SS15 of this
// task).
//
// Per SCRIPT-RUNTIME-001 §71 ("Missing inputs... yields bounded error or
// Unknown. The runtime must not substitute zero or a prior result
// silently."), a prospect who HAS a subject but no numeric value for this
// field/option makes the derived total unavailable — {available: false} is
// returned instead of silently treating the missing input as zero. A field
// with no prospect subjects at all still derives to zero legitimately:
// there is no missing input to substitute for, only an empty sum.
function deriveHouseholdValue(field, householdOptionIndex, prospectSubjects, valuesByKey) {
  let total = 0;
  let hasMissingInput = false;
  prospectSubjects.forEach((subject) => {
    const raw = valuesByKey.get(`${field.id}::${subject.id}::${householdOptionIndex}`);
    const n = parseFloat(raw);
    if (Number.isFinite(n)) {
      total += n;
    } else {
      hasMissingInput = true;
    }
  });
  if (hasMissingInput) return { available: false, value: null };
  return { available: true, value: String(total) };
}

/**
 * Renders the editable control appropriate to a field's field_type. Reuses
 * the same primitives (and the same per-type mapping) as the Discovery
 * tool: text/number -> Input, textarea -> Textarea, dropdown -> Select
 * (using field.options). No parallel form-control system.
 */
function QuoteFieldControl({ id, field, value, onChange }) {
  const stringValue = value ?? "";

  switch (field.field_type) {
    case "dropdown":
      return (
        <Select value={stringValue} onValueChange={onChange}>
          <SelectTrigger id={id} className={FIELD_CONTROL_CLASS}>
            <SelectValue placeholder="Select…" />
          </SelectTrigger>
          <SelectContent>
            {(field.options || []).map((option) => (
              <SelectItem key={option} value={option}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    case "textarea":
      return (
        <Textarea
          id={id}
          className={FIELD_TEXTAREA_CLASS}
          value={stringValue}
          onChange={(event) => onChange(event.target.value)}
        />
      );
    case "number":
      return (
        <Input
          id={id}
          type="number"
          className={FIELD_CONTROL_CLASS}
          value={stringValue}
          onChange={(event) => onChange(event.target.value)}
        />
      );
    case "text":
    default:
      return (
        <Input
          id={id}
          type="text"
          className={FIELD_CONTROL_CLASS}
          value={stringValue}
          onChange={(event) => onChange(event.target.value)}
        />
      );
  }
}

function QuoteField({ field, subjectId, optionIndex, value, onChange }) {
  const id = controlId(field, subjectId, optionIndex);
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-xs text-slate-500">
        {field.label}
      </label>
      <QuoteFieldControl id={id} field={field} value={value} onChange={onChange} />
    </div>
  );
}

// Household's own field row: same label treatment, but a read-only,
// visually dimmer control — the agent can look at, but never edit, a
// derived aggregate directly (WORKSPACE-AUDIT-004 SS11). When the
// derivation is unavailable (SCRIPT-RUNTIME-001 §71 — a missing prospect
// input, not a fabricated zero), the control displays "Unavailable" rather
// than a numeric value, and is flagged for direct testability.
function QuoteHouseholdField({ field, derivation }) {
  const id = controlId(field, HOUSEHOLD_SUBJECT_ID, "derived");
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-xs text-slate-500">
        {field.label}
      </label>
      {derivation.available ? (
        <Input id={id} type="text" readOnly value={derivation.value} className={READONLY_CONTROL_CLASS} />
      ) : (
        <Input
          id={id}
          type="text"
          readOnly
          value="Unavailable"
          className={`${READONLY_CONTROL_CLASS} italic text-slate-500`}
          data-testid={`workspace-quote-household-unavailable-${field.id}`}
        />
      )}
    </div>
  );
}

/**
 * Compact Agent Toolbar Quote panel — a subject-aware, option-aware,
 * editable-for-prospects / derived-for-Household prototype of the future
 * live Quote tool. Fixture/local-state driven, deterministic, clearly-
 * synthetic values only — no fetch, persistence, or API behavior.
 *
 * Architecture, per WORKSPACE-AUDIT-004:
 *   - Subjects: Household (derived) + N prospect subjects, same shape
 *     Discovery already established (WORKSPACE-AUDIT-003) — no "All"
 *     option, matching the real Quote Sheet's own "For:" selector.
 *   - Options: a fixed [1, 2, 3] set (not authored, matching the real
 *     system's hardcoded three options).
 *   - Canonical value identity: field_id + subject_id + option_index.
 *   - Household owns no persisted/local values of its own — its fields are
 *     derived at render time by summing every prospect's value for that
 *     field at HOUSEHOLD's own selected option (not each prospect's own
 *     individually-selected option).
 *
 * One deliberate departure from the current Script Runner implementation
 * (see WORKSPACE-AUDIT-004 SS3 and this task's product-decision section):
 * each subject remembers its OWN selected option independently for the
 * life of this mounted component, rather than resetting to Option 1 every
 * time the "For:" subject changes. This is a considered product decision
 * for Agent Workspace, not an oversight — Script Runner's current global,
 * subject-switch-resets-option behavior is explicitly NOT reproduced here.
 *
 * No calculated/formula fields, no progressive disclosure, and no
 * commission-catalog integration exist in this first pass — see
 * WORKSPACE-AUDIT-004 SS15 for why those are intentionally deferred.
 *
 * Deliberately receives its subject/option/field/value data as props
 * (defaulted from the fixture) rather than only reading the fixture module
 * internally, so a later Boundary-A-authorized real selector/hook can
 * supply `subjects`, `options`, `fields`, and `values` in the exact same
 * shape without redesigning this presentation.
 */
export default function QuotePanel({
  subjects,
  options,
  fields,
  values,
  projectionStatus,
}) {
  // AW-ORIGINAL-RESTORE-001. Required, fail-closed. Hooks below are
  // seeded from safe empties because they run before the guard returns.
  const renderStatus = resolveToolRenderStatus(projectionStatus, [
    subjects,
    options,
    fields,
    values,
  ]);
  const renderable = mayRender(renderStatus);
  const safeSubjects = renderable ? subjects : EMPTY_LIST;
  const safeFields = renderable ? fields : EMPTY_LIST;
  const safeOptions = renderable ? options : EMPTY_LIST;

  const [selectedSubjectId, setSelectedSubjectId] = useState(HOUSEHOLD_SUBJECT_ID);
  const [selectedOptionBySubject, setSelectedOptionBySubject] = useState({});
  const [valuesByKey, setValuesByKey] = useState(() =>
    renderable ? valuesToMap(values) : new Map()
  );

  const selectedOptionIndex = selectedOptionBySubject[selectedSubjectId] ?? safeOptions[0];

  const setOptionForSubject = (subjectId, optionIndex) => {
    setSelectedOptionBySubject((prev) => ({ ...prev, [subjectId]: optionIndex }));
  };

  const handleFieldChange = (fieldId, subjectId, optionIndex, nextValue) => {
    setValuesByKey((prev) => {
      const next = new Map(prev);
      next.set(`${fieldId}::${subjectId}::${optionIndex}`, nextValue);
      return next;
    });
  };

  const orderedFields = useMemo(
    () => [...safeFields].sort((a, b) => a.sort_order - b.sort_order),
    [safeFields]
  );
  const householdFields = useMemo(
    () => orderedFields.filter((field) => !!field.include_in_household),
    [orderedFields]
  );
  const prospectSubjects = useMemo(() => sortedProspectSubjects(safeSubjects), [safeSubjects]);

  const subjectOptions = useMemo(
    () => [{ id: HOUSEHOLD_SUBJECT_ID, name: "Household" }, ...prospectSubjects],
    [prospectSubjects]
  );

  if (!renderable) {
    return (
      <ToolProjectionNotice
        status={renderStatus.status}
        reason={renderStatus.reason}
        testIdPrefix="workspace-quote"
      />
    );
  }

  if (orderedFields.length === 0) {
    return (
      <p className="text-xs text-slate-500" data-testid="workspace-quote-empty">
        No quote fields for this script.
      </p>
    );
  }

  const isHousehold = selectedSubjectId === HOUSEHOLD_SUBJECT_ID;
  const fieldsToShow = isHousehold ? householdFields : orderedFields;

  return (
    <div className="flex flex-col gap-3" data-testid="workspace-quote-panel">
      <div className="flex items-center gap-2">
        <span className="shrink-0 text-[11px] font-medium text-slate-500">For</span>
        <Select value={selectedSubjectId} onValueChange={setSelectedSubjectId}>
          <SelectTrigger
            className={`flex-1 ${FIELD_CONTROL_CLASS}`}
            data-testid="workspace-quote-subject-filter"
            aria-label="Quote for"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {subjectOptions.map((option) => (
              <SelectItem key={option.id} value={option.id}>
                {option.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center gap-2">
        <span className="shrink-0 text-[11px] font-medium text-slate-500">Option</span>
        {/* Segmented-pill treatment matches the established Script Runner
            Quote Sheet Option 1/2/3 control (the Script Runner page's own
            "quote-option-row" block: an outer rounded-full bg-white/5
            ring-1 ring-white/10 shell around indigo-tinted segments —
            bg-indigo-500/15/text-indigo-100 inactive, bg-indigo-500/90/
            text-white active) reproduced locally on the existing ToggleGroup
            primitive rather than importing that page's own Button-based
            markup, per Boundary A. Segments are flex-1 (evenly fill the row)
            and the inter-segment gap is tighter than the source page's
            literal gap-2 so the three full "Option N" labels read as one
            cohesive control in the narrower Agent Toolbar. */}
        <ToggleGroup
          type="single"
          value={String(selectedOptionIndex)}
          onValueChange={(v) => {
            if (v) setOptionForSubject(selectedSubjectId, Number(v));
          }}
          data-testid="workspace-quote-option-toggle"
          className="flex-1 justify-start gap-1 rounded-full bg-white/5 p-1 ring-1 ring-white/10"
        >
          {options.map((opt) => (
            <ToggleGroupItem
              key={opt}
              value={String(opt)}
              size="sm"
              aria-label={`Option ${opt}`}
              data-testid={`workspace-quote-option-${opt}`}
              className="h-7 flex-1 rounded-full px-2 text-[11px] font-medium text-indigo-100 bg-indigo-500/15 hover:bg-indigo-500/25 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-indigo-400 data-[state=on]:bg-indigo-500/90 data-[state=on]:text-white data-[state=on]:hover:bg-indigo-500"
            >
              {`Option ${opt}`}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>

      <div className="flex flex-col gap-3" data-testid="workspace-quote-fields">
        {isHousehold
          ? fieldsToShow.map((field) => (
              <QuoteHouseholdField
                key={field.id}
                field={field}
                derivation={deriveHouseholdValue(field, selectedOptionIndex, prospectSubjects, valuesByKey)}
              />
            ))
          : fieldsToShow.map((field) => (
              <QuoteField
                key={field.id}
                field={field}
                subjectId={selectedSubjectId}
                optionIndex={selectedOptionIndex}
                value={valuesByKey.get(`${field.id}::${selectedSubjectId}::${selectedOptionIndex}`)}
                onChange={(nextValue) =>
                  handleFieldChange(field.id, selectedSubjectId, selectedOptionIndex, nextValue)
                }
              />
            ))}
      </div>
    </div>
  );
}
